import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	inject,
	OnInit,
	signal,
	computed,
	viewChild,
	AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ProductCostsApiService } from '../product-costs.api.service';
import {
	ProductCost,
	ProductCostCurrency,
	ProductCostKind,
	ProductCostMethod,
	ProductCostPayload,
	ProductCostPaymentMethod,
	PRODUCT_COST_METHOD_LABELS,
	ProductCostPrice,
} from '../product-costs.model';
import { ProductService } from '@/products/product/product.service';
import { GenericMatAutocompleteComponent } from '@/common/generic-mat-autocomplete/generic-mat-autocomplete.component';
import { environment } from 'src/environments/environment';
import { forkJoin, finalize, skip, switchMap } from 'rxjs';

const DEFAULT_APPEARANCE: MatFormFieldAppearance = 'outline';

interface ProductCostDialogData {
	productId: number;
	cost?: ProductCost;
}

type ProductCostFormValue = Omit<ProductCost, 'id' | 'currency' | 'paymentMethod'> & {
	currency: number | null;
	paymentMethod: number | null;
};

type ProductCostFormGroup = {
	[Key in keyof ProductCostFormValue]: FormControl<ProductCostFormValue[Key]>;
};

type ProductCostSelectionSnapshot = Omit<ProductCostPayload, 'paymentMethod'> & {
	paymentMethod: number | null;
};

@Component({
	selector: 'app-product-cost-add-dialog',
	standalone: true,
	imports: [
		MatDialogModule,
		MatButtonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatProgressSpinnerModule,
		MatSelectModule,
		MatSlideToggleModule,
		GenericMatAutocompleteComponent,
	],
	templateUrl: './product-cost-add-dialog.component.html',
	styleUrl: './product-cost-add-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCostAddDialogComponent implements OnInit, AfterViewInit {
	form!: FormGroup<ProductCostFormGroup>;
	readonly appearance: MatFormFieldAppearance = DEFAULT_APPEARANCE;
	readonly kindOptions = signal<ProductCostKind[]>([]);
	readonly currencies = signal<ProductCostCurrency[]>([]);
	readonly paymentMethods = signal<ProductCostPaymentMethod[]>([]);
	readonly methodOptions = Object.entries(PRODUCT_COST_METHOD_LABELS).map(
		([methodValue, methodLabel]: [string, string]) => ({
			value: methodValue as ProductCostMethod,
			label: methodLabel,
		}),
	);
	readonly isEditMode = computed(() => !!this.cost());

	readonly saving = signal(false);

	readonly cost = signal<ProductCost | undefined>(inject<ProductCostDialogData>(MAT_DIALOG_DATA).cost);

	readonly productCostsApiUrl = `${environment.apiUrl}/product/cost/`;

	readonly costSelectedFromList = signal(false);

	readonly selectedCostId = signal<number | null>(null);

	readonly autocompleteRef = viewChild(GenericMatAutocompleteComponent);

	private selectedCostSnapshot: ProductCostSelectionSnapshot | null = null;

	private readonly fb = inject(FormBuilder);
	private readonly dialogRef = inject(MatDialogRef<ProductCostAddDialogComponent>);
	private readonly productCostsApiService = inject(ProductCostsApiService);
	private readonly productService = inject(ProductService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly productId: number = inject<ProductCostDialogData>(MAT_DIALOG_DATA).productId;

	readonly productCostResponseMapper = (apiResponse: unknown): ProductCost[] =>
		(apiResponse as { results?: ProductCost[] })?.results ?? [];

	compareKind(a: ProductCostKind | null, b: ProductCostKind | null): boolean {
		return a?.id === b?.id;
	}

	onCostSelected(cost: ProductCost | null): void {
		if (!cost) {
			return;
		}
		this.form.patchValue({
			name: cost.name,
			kind: cost.kind,
			currency: cost.currency?.id ?? null,
			method: cost.method,
			fixedCapacity: cost.fixedCapacity,
			isFaculty: cost.isFaculty,
			note: cost.note,
			paymentMethod: cost.paymentMethod?.id ?? null,
			prices: cost.prices ?? [],
		});
		this.costSelectedFromList.set(true);
		this.selectedCostId.set(cost.id);
		this.selectedCostSnapshot = {
			name: cost.name,
			kind: cost.kind.id,
			currency: cost.currency.id,
			method: cost.method,
			fixedCapacity: cost.fixedCapacity,
			isFaculty: cost.isFaculty,
			note: cost.note,
			paymentMethod: cost.paymentMethod?.id ?? null,
			prices: cost.prices ?? [],
		};
	}

	cancel(): void {
		this.dialogRef.close();
	}

	save(): void {
		if (this.isEditMode()) {
			this.update();
		} else {
			this.add();
		}
	}

	ngOnInit(): void {
		this.form = this.fb.group<ProductCostFormGroup>({
			name: new FormControl('', Validators.required),
			kind: new FormControl<ProductCostKind | null>(null, Validators.required),
			currency: new FormControl<number | null>(null, Validators.required),
			method: new FormControl<ProductCostMethod | null>(null, Validators.required),
			fixedCapacity: new FormControl<number | null>(null),
			isFaculty: new FormControl<boolean>(false),
			note: new FormControl<string | null>(null),
			paymentMethod: new FormControl<number | null>(null, Validators.required),
			prices: new FormControl<ProductCostPrice[]>([]),
		});
		this.form.valueChanges
			.pipe(skip(1), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.checkIfUserModifiedSelection());
		this.form
			.get('method')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.updateFixedCapacityState());
		forkJoin({
			currencies: this.productCostsApiService.getCurrencies(),
			kindOptions: this.productCostsApiService.getCostKinds(),
			paymentMethods: this.productCostsApiService.getPaymentMethods(),
		})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(({ currencies, kindOptions, paymentMethods }) => {
				this.currencies.set(currencies);
				this.kindOptions.set(kindOptions);
				this.paymentMethods.set(paymentMethods);
				const cost = this.cost();
				if (cost) {
					this.form.patchValue({
						name: cost.name,
						kind: cost.kind,
						currency: cost.currency?.id ?? null,
						method: cost.method,
						fixedCapacity: cost.fixedCapacity,
						isFaculty: cost.isFaculty,
						note: cost.note,
						paymentMethod: cost.paymentMethod?.id ?? null,
						prices: cost.prices ?? [],
					});
				}
			});
	}

	ngAfterViewInit(): void {
		const autocomplete = this.autocompleteRef();
		if (this.isEditMode() || !autocomplete) {
			return;
		}
		autocomplete.searchControl.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((autocompleteInputValue) => {
				if (typeof autocompleteInputValue === 'string') {
					this.form.get('name')?.setValue(autocompleteInputValue);
				}
			});
	}

	private updateFixedCapacityState(): void {
		const method = this.form.get('method')?.value;
		const fixedCapacityControl = this.form.get('fixedCapacity');
		if (!fixedCapacityControl) {
			return;
		}
		if (method === ProductCostMethod.PER_PERSON) {
			fixedCapacityControl.setValue(null);
			fixedCapacityControl.disable();
		} else {
			fixedCapacityControl.enable();
		}
	}

	private checkIfUserModifiedSelection(): void {
		if (!this.costSelectedFromList() || this.selectedCostSnapshot === null) {
			return;
		}
		const currentFormValue = this.form.getRawValue();
		const snapshotAtSelection = this.selectedCostSnapshot;
		const formValueDiffersFromSelection =
			currentFormValue.name !== snapshotAtSelection.name ||
			(currentFormValue.kind?.id ?? null) !== snapshotAtSelection.kind ||
			currentFormValue.currency !== snapshotAtSelection.currency ||
			currentFormValue.method !== snapshotAtSelection.method ||
			currentFormValue.fixedCapacity !== snapshotAtSelection.fixedCapacity ||
			currentFormValue.isFaculty !== snapshotAtSelection.isFaculty ||
			currentFormValue.note !== snapshotAtSelection.note ||
			currentFormValue.paymentMethod !== snapshotAtSelection.paymentMethod;
		if (formValueDiffersFromSelection) {
			this.costSelectedFromList.set(false);
		}
	}

	private add(): void {
		const existingCostIds = (this.productService.data.costs ?? []).map((item: ProductCost) => item.id);
		if (this.costSelectedFromList() && this.selectedCostId() !== null) {
			this.saving.set(true);
			const costIdsWithSelected = [...existingCostIds, this.selectedCostId()!];
			this.productCostsApiService
				.patchProductCosts(this.productId, { costs: costIdsWithSelected })
				.pipe(finalize(() => this.saving.set(false)))
				.subscribe((productWithUpdatedCostsList) => {
					const costs = productWithUpdatedCostsList?.['costs'];
					this.productService.data.costs = costs ?? [];
					this.dialogRef.close();
				});
			return;
		}
		this.saving.set(true);
		const formValue = this.form.getRawValue();
		const payload: ProductCostPayload = {
			name: formValue.name,
			kind: formValue.kind!.id,
			currency: formValue.currency!,
			method: formValue.method!,
			fixedCapacity: formValue.fixedCapacity,
			isFaculty: formValue.isFaculty,
			note: formValue.note,
			paymentMethod: formValue.paymentMethod!,
			prices: [],
		};
		this.productCostsApiService
			.createProductCost(payload)
			.pipe(
				switchMap((newlyCreatedCost) => {
					const costIdsWithNewCost = [...existingCostIds, newlyCreatedCost.id];
					return this.productCostsApiService.patchProductCosts(this.productId, {
						costs: costIdsWithNewCost,
					});
				}),
				finalize(() => this.saving.set(false)),
			)
			.subscribe((productWithUpdatedCostsList) => {
				const costs = productWithUpdatedCostsList?.['costs'];
				this.productService.data.costs = costs ?? [];
				this.dialogRef.close();
			});
	}

	private update(): void {
		const cost = this.cost();
		if (!cost) {
			return;
		}
		const formValue = this.form.getRawValue();
		const payload: ProductCostPayload = {
			name: formValue.name,
			kind: formValue.kind!.id,
			currency: formValue.currency!,
			method: formValue.method!,
			fixedCapacity: formValue.fixedCapacity,
			isFaculty: formValue.isFaculty,
			note: formValue.note,
			paymentMethod: formValue.paymentMethod!,
			prices: this.cost()?.prices ?? [],
		};
		this.saving.set(true);
		this.productCostsApiService
			.updateProductCost(cost.id, payload)
			.pipe(finalize(() => this.saving.set(false)))
			.subscribe((updatedCost) => {
				const costs: ProductCost[] = this.productService.data.costs ?? [];
				this.productService.data.costs = costs.map((pc) => (pc.id === updatedCost.id ? updatedCost : pc));
				this.dialogRef.close();
			});
	}
}
